import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAppSelector } from 'hooks';
import { trackPendoEvent } from 'services/PendoService';
import { getAvatarUrl } from 'utils/media';

import AvatarFrame from 'components/Streaks/AvatarFrame';
import { FREEZE_PRICE } from 'components/Streaks/BuyFreezes/payment';
import { StreakSnapshot } from 'components/Streaks/model/types';
import { useStreakReducedMotion } from 'components/Streaks/useStreakReducedMotion';

import styles from './StreaksIntro.module.scss';

/** Fired by the panel's "How streaks work" link (AccountStreakBlock). */
export const OPEN_STREAKS_INTRO_EVENT = 'open-streaks-intro';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * "How streaks work" (EN-11239, canvas Intro / IntroCard): the status ladder
 * framing the player's own avatar, and the three rules, from the live
 * milestones and flop target. The explainer only (JRam, 10/27): one button to
 * close. The first-launch showing and its notifications ending come with the
 * pushes; "Everyone starts at day 0 today" is launch-day copy, so it is left out.
 */
export default function StreaksIntro({ snapshot }: { snapshot: StreakSnapshot }) {
  const [open, setOpen] = useState(false);
  const reducedMotion = useStreakReducedMotion();
  const avatar = useAppSelector((s) => s.user.playerAvatar);
  const playerGUID = useAppSelector((s) => s.user.playerGUID);
  const avatarSrc = useMemo(() => (avatar ? getAvatarUrl(avatar) : undefined), [avatar]);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_STREAKS_INTRO_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_STREAKS_INTRO_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    if (playerGUID)
      trackPendoEvent({
        playerGUID,
        eventName: 'streaks_intro_opened',
        properties: { from: 'panel' }
      });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, playerGUID]);

  const close = useCallback(() => setOpen(false), []);

  if (!open) return null;
  const ladder = [...snapshot.milestones].sort((a, b) => a.days - b.days);
  return ReactDOM.createPortal(
    <div
      className={`${styles.backdrop} ${reducedMotion ? styles.still : ''}`}
      onClick={close}
      data-testid='streaks-intro'>
      <section
        className={styles.dialog}
        role='dialog'
        aria-modal='true'
        aria-labelledby='streaks-intro-title'
        onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <h2 id='streaks-intro-title' className={styles.title}>
            Streaks are here.
            <br />
            Play every day.
          </h2>
          <button
            ref={closeRef}
            type='button'
            className={styles.close}
            aria-label='Close'
            onClick={close}>
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.2'
              strokeLinecap='round'
              aria-hidden='true'>
              <path d='M6 6l12 12M18 6L6 18' />
            </svg>
          </button>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardTitle}>How it works</span>
            <span className={styles.games}>Cash, Sit &amp; Go, Tournaments</span>
          </div>

          <ul className={styles.ladder} aria-label='Statuses'>
            {ladder.map((m) => (
              <li key={m.key} className={styles.rung}>
                <AvatarFrame tier={m.frameKey} size={40} src={avatarSrc} />
                <span className={styles.rungName}>{m.name}</span>
                <span className={styles.rungDays}>{plural(m.days, 'day')}</span>
              </li>
            ))}
          </ul>

          <ol className={styles.rules}>
            <li>
              <strong>See {plural(snapshot.flopsNeeded, 'flop')}</strong> before midnight CT and the
              day counts.
            </li>
            <li>
              Days in a row earn statuses. Each one frames your avatar and unlocks avatars you keep
              for good.
            </li>
            <li>
              Miss a day? A freeze covers it: one free with each status, ${FREEZE_PRICE} each after.
            </li>
          </ol>
        </div>

        <button type='button' className={styles.done} onClick={close}>
          Got it
        </button>
      </section>
    </div>,
    document.body
  );
}
